import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import FormHelperText from '@material-ui/core/FormHelperText'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { ThemeProvider } from '@mui/material/styles'
import { DateTime } from 'luxon'
import CustomLuxonAdapter from 'lib/CustomLuxonAdapter'
import PgaKeyboardDatePicker from 'components/pga-time-picker/pga-keyboard-date-picker'
import { mergeDesktopDatePickerSlotProps } from 'components/pga-time-picker/desktop-date-picker'
import { calendarDatePickerTheme } from 'pages/pga-coach/bookings/schedule/calendar/calendar-date-picker-theme'
import { LESSON_WINDOW_OPTIONS } from '../constants'
import {
  lessonRangeError,
  lessonWindowRange,
  lessonWindowValue,
} from '../format'
import useStyles from '../styles'
import { FilterSelect } from './filter-field'
import { pickerIconButton, pickerTextField } from './picker-props'

const toPickerValue = iso => (iso ? DateTime.fromISO(iso) : null)

// "Lessons on certain dates": has a lesson with the coach in this window. Choosing dates keeps whole days:
// From starts its day and To ends its day, in the browser's time zone.
export default function LessonWindowFilter({ filters, onChange }) {
  const classes = useStyles()
  // Kept locally because "Choose dates" with both dates empty looks the same
  // as "Any time" in the filters.
  const [value, setValue] = useState(() => lessonWindowValue(filters))
  const rangeError = lessonRangeError(filters)

  const select = next => {
    setValue(next)
    if (next !== 'custom') onChange(lessonWindowRange(next))
  }

  const pickDate = (key, edge) => picked => {
    if (picked === null) return onChange({ [key]: null })
    if (!picked?.isValid) return
    onChange({ [key]: picked[edge]('day').toISO() })
  }

  return (
    <FilterSelect
      id="campaign-lesson-window"
      label="Lessons on certain dates"
      description="Students with a lesson in that time, past or upcoming."
      value={value}
      options={LESSON_WINDOW_OPTIONS}
      onChange={select}
    >
      {value === 'custom' ? (
        <Box>
          <ThemeProvider theme={calendarDatePickerTheme}>
            <LocalizationProvider
              dateAdapter={CustomLuxonAdapter}
              locale="en-us"
            >
              <Box className={classes.dateTimeFields}>
                <PgaKeyboardDatePicker
                  label="From"
                  format="MMM d, yyyy"
                  value={toPickerValue(filters.lessonFrom)}
                  onChange={pickDate('lessonFrom', 'startOf')}
                  slotProps={mergeDesktopDatePickerSlotProps({
                    textField: pickerTextField,
                    openPickerButton: pickerIconButton,
                  })}
                />
                <PgaKeyboardDatePicker
                  label="To"
                  format="MMM d, yyyy"
                  value={toPickerValue(filters.lessonTo)}
                  onChange={pickDate('lessonTo', 'endOf')}
                  slotProps={mergeDesktopDatePickerSlotProps({
                    textField: {
                      ...pickerTextField,
                      error: Boolean(rangeError),
                    },
                    openPickerButton: pickerIconButton,
                  })}
                />
              </Box>
            </LocalizationProvider>
          </ThemeProvider>
          <FormHelperText error={Boolean(rangeError)}>
            {rangeError ||
              'Leave a date empty for no limit. Uses your computer’s time zone.'}
          </FormHelperText>
        </Box>
      ) : null}
    </FilterSelect>
  )
}

LessonWindowFilter.propTypes = {
  filters: PropTypes.shape({
    lessonFrom: PropTypes.string,
    lessonTo: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
}
